import React, { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import UserRoleGuardContainer from '../../../../../auth/components/UserRoleGuard/container';
import { Alert } from '../../../../../components/CiscoUi';
import { HelpButtonContainer } from '../../../../../components/HelpButton/container.tsx';
import { PageHeading } from '../../../../../components/PageHeading';
import { NAT_RULES_HELP_LINKS } from '../../../../../constants/user-guide';
import { useGetVmNicTargetsQuery } from '../../../../../redux/configuration/traffic/api';
import { INTERNAL_USER_ROLES } from '../../../../constants.ts';
import { NatRuleDeleteModal } from './components/NatRuleDeleteModal';
import { ConnectedNatRulesAddForm } from './components/NatRulesAddForm/container';
import { NatRulesTable } from './components/NatRulesTable';
import { NatRulesContainerProps, NatRulesRTKProps } from './container';

export type NatRulesProps = NatRulesContainerProps & NatRulesRTKProps;

export function NatRules({
  isError,
  isLoading,
  natRuleToDelete,
  natRules,
  setNatRuleToDelete,
}: NatRulesProps): ReactElement | null {
  const { t } = useTranslation();
  const { uid: topologyUid } = useParams<TopologyUid>();
  const { data: vmTargetsData } = useGetVmNicTargetsQuery(topologyUid);

  const onDeleteClick = useCallback(
    (natRule: NatRule) => {
      setNatRuleToDelete(natRule);
    },
    [setNatRuleToDelete],
  );

  return (
    <section className="half-padding-left">
      <PageHeading pageTitle={t('traffic.natRules.name')} />
      <UserRoleGuardContainer requiredRoles={INTERNAL_USER_ROLES}>
        <ConnectedNatRulesAddForm internalIps={vmTargetsData?.internalIps} />
      </UserRoleGuardContainer>
      {!natRules?.length && !isLoading ? (
        <Alert colour="info">{t('traffic.natRules.empty')}</Alert>
      ) : (
        <NatRulesTable
          onDeleteClick={onDeleteClick}
          natRules={natRules || []}
          isError={isError}
          isLoading={isLoading}
        />
      )}
      {natRuleToDelete && (
        <NatRuleDeleteModal
          setNatRuleToDelete={setNatRuleToDelete}
          natRule={natRuleToDelete}
        />
      )}
      <HelpButtonContainer links={NAT_RULES_HELP_LINKS} />
    </section>
  );
}
